import * as React from 'react';

import {
  CurrencyCode,
  Money,
  OperationType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { weightToPounds } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { NormalizedLineItem } from '../../_shared/ItemsInterface/_types';

import { ItemPriceRecord } from '~/modules/pricing/types/pricing';
import { $lazyResolveItemPrice } from '~/modules/trade/_state/scaleTicketPro.state';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { convertWeightForPricing } from '~/modules/trade/utils/weight';

type UseAutoPriceParams = {
  facilityId: string;
  supplierAccountId: string | null;
  currencyCode: CurrencyCode;
  lineItems: NormalizedLineItem[];
  enabled: boolean;
};

export type PendingContextChange = {
  overriddenCount: number;
};

type UseAutoPriceResult = {
  resolvedPrices: Map<string, Money | null>;
  isResolving: boolean;
  markUserOverride: (lineItemId: string) => void;
  pendingContextChange: PendingContextChange | null;
  confirmContextChange: (clearOverrides: boolean) => void;
};

type PrevItemSnapshot = {
  itemId: string | null;
  netWeight: number;
  pricingUnit: string;
};

function selectTierPrice(
  priceRecord: ItemPriceRecord,
  pricingUnit: string,
  netWeightInPounds: number,
): number | null {
  const matchingPricing =
    priceRecord.itemPricings.find(
      (ep) => ep.itemPricing.pricingUnit === pricingUnit,
    ) ?? priceRecord.itemPricings[0];

  if (!matchingPricing || matchingPricing.evaluatedTiers.length === 0) {
    return null;
  }

  const convertedWeight = convertWeightForPricing(
    netWeightInPounds,
    pricingUnit,
  );

  const matchingTier = matchingPricing.evaluatedTiers.find((et) => {
    const max = et.tier.maxAmount;
    return max === null || convertedWeight <= max;
  });

  return (
    (matchingTier ?? matchingPricing.evaluatedTiers[0])?.targetPrice ?? null
  );
}

export function useAutoPrice(params: UseAutoPriceParams): UseAutoPriceResult {
  const { facilityId, supplierAccountId, currencyCode, lineItems, enabled } =
    params;

  const { fetch: fetchPrice } = useAtomValue($lazyResolveItemPrice);

  const [resolvedPrices, setResolvedPrices] = React.useState<
    Map<string, Money | null>
  >(() => new Map());
  const [isResolving, setIsResolving] = React.useState(false);
  const [pendingContextChange, setPendingContextChange] =
    React.useState<PendingContextChange | null>(null);

  const priceCacheRef = React.useRef(new Map<string, ItemPriceRecord>());
  const userOverridesRef = React.useRef(new Set<string>());
  const prevContextRef = React.useRef({
    facilityId,
    supplierAccountId,
    currencyCode,
  });
  const prevItemsRef = React.useRef(new Map<string, PrevItemSnapshot>());
  const generationRef = React.useRef(0);
  const forceResolveRef = React.useRef(false);
  const [resolveNonce, setResolveNonce] = React.useState(0);

  const markUserOverride = React.useCallback((lineItemId: string) => {
    userOverridesRef.current.add(lineItemId);
    setResolvedPrices((prev) => {
      if (!prev.has(lineItemId)) return prev;
      const next = new Map(prev);
      next.delete(lineItemId);
      return next;
    });
  }, []);

  const confirmContextChange = React.useCallback((clearOverrides: boolean) => {
    if (clearOverrides) {
      userOverridesRef.current.clear();
    }
    forceResolveRef.current = true;
    setPendingContextChange(null);
    setResolveNonce((n) => n + 1);
  }, []);

  React.useEffect(() => {
    if (!enabled) return;

    const generation = ++generationRef.current;
    let forceResolveAll = false;

    type ResolveItem = { lineItem: NormalizedLineItem; cacheKey: string };
    const itemsToResolve: ResolveItem[] = [];
    const itemsToRetier: ResolveItem[] = [];

    if (forceResolveRef.current) {
      forceResolveRef.current = false;
      priceCacheRef.current.clear();
      prevContextRef.current = { facilityId, supplierAccountId, currencyCode };
      forceResolveAll = true;
    } else {
      const contextChanged =
        prevContextRef.current.facilityId !== facilityId ||
        prevContextRef.current.supplierAccountId !== supplierAccountId ||
        prevContextRef.current.currencyCode !== currencyCode;

      if (contextChanged) {
        priceCacheRef.current.clear();

        if (userOverridesRef.current.size > 0) {
          setPendingContextChange({
            overriddenCount: userOverridesRef.current.size,
          });
          const newPrevItems = new Map<string, PrevItemSnapshot>();
          for (const item of lineItems) {
            newPrevItems.set(item.id, {
              itemId: item.itemId,
              netWeight: item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
              pricingUnit: item.pricingUnit,
            });
          }
          prevItemsRef.current = newPrevItems;
          return;
        }

        prevContextRef.current = {
          facilityId,
          supplierAccountId,
          currencyCode,
        };
        forceResolveAll = true;
      }
    }

    for (const item of lineItems) {
      if (!item.itemId) continue;
      if (userOverridesRef.current.has(item.id)) continue;

      const cacheKey = `${item.itemId}:${item.pricingUnit}`;

      if (forceResolveAll) {
        itemsToResolve.push({ lineItem: item, cacheKey });
        continue;
      }

      const prevItem = prevItemsRef.current.get(item.id);

      if (!prevItem) {
        if (item.unitPrice === null) {
          itemsToResolve.push({ lineItem: item, cacheKey });
        }
      } else if (
        prevItem.itemId !== item.itemId ||
        prevItem.pricingUnit !== item.pricingUnit
      ) {
        userOverridesRef.current.delete(item.id);
        itemsToResolve.push({ lineItem: item, cacheKey });
      } else if (
        prevItem.netWeight !==
        (item.weights?.[enumLoadWeightType.Net]?.value ?? 0)
      ) {
        if (priceCacheRef.current.has(cacheKey)) {
          itemsToRetier.push({ lineItem: item, cacheKey });
        } else {
          itemsToResolve.push({ lineItem: item, cacheKey });
        }
      }
    }

    const newPrevItems = new Map<string, PrevItemSnapshot>();
    for (const item of lineItems) {
      newPrevItems.set(item.id, {
        itemId: item.itemId,
        netWeight: item.weights?.[enumLoadWeightType.Net]?.value ?? 0,
        pricingUnit: item.pricingUnit,
      });
    }
    prevItemsRef.current = newPrevItems;

    const newPrices = new Map<string, Money | null>();

    for (const { lineItem, cacheKey } of itemsToRetier) {
      const cached = priceCacheRef.current.get(cacheKey);
      if (!cached) continue;

      const netWeightPounds = weightToPounds(
        lineItem.weights?.[enumLoadWeightType.Net],
      );
      const price = selectTierPrice(
        cached,
        lineItem.pricingUnit,
        netWeightPounds,
      );

      if (price !== null) {
        newPrices.set(lineItem.id, {
          amount: price,
          currencyCode,
          __typename: 'Money',
        });
      }
    }

    if (itemsToResolve.length === 0) {
      if (newPrices.size > 0 && generationRef.current === generation) {
        setResolvedPrices((prev) => {
          const merged = new Map(prev);
          for (const [k, v] of newPrices) merged.set(k, v);
          return merged;
        });
      }
      return;
    }

    setIsResolving(true);

    (async () => {
      try {
        for (const { lineItem, cacheKey } of itemsToResolve) {
          if (generationRef.current !== generation) return;
          if (!lineItem.itemId) continue;

          let priceRecord = priceCacheRef.current.get(cacheKey) ?? null;

          if (!priceRecord) {
            try {
              priceRecord = await fetchPrice({
                facilityId,
                itemId: lineItem.itemId,
                operationType: 'Purchase' as OperationType,
                currency: currencyCode,
                counterpartyAccountId: supplierAccountId ?? undefined,
                displayPricingUnit: lineItem.pricingUnit as Unit,
              });

              if (priceRecord) {
                priceCacheRef.current.set(cacheKey, priceRecord);
              }
            } catch (err) {
              console.error(
                'Failed to resolve price for',
                lineItem.itemId,
                err,
              );
              continue;
            }
          }

          if (!priceRecord) {
            newPrices.set(lineItem.id, null);
            continue;
          }

          const netWeightPounds = weightToPounds(
            lineItem.weights?.[enumLoadWeightType.Net],
          );
          const price = selectTierPrice(
            priceRecord,
            lineItem.pricingUnit,
            netWeightPounds,
          );

          if (price !== null) {
            newPrices.set(lineItem.id, {
              amount: price,
              currencyCode,
              __typename: 'Money',
            });
          } else {
            newPrices.set(lineItem.id, null);
          }
        }

        if (generationRef.current !== generation) return;

        if (newPrices.size > 0) {
          setResolvedPrices((prev) => {
            const merged = new Map(prev);
            for (const [k, v] of newPrices) merged.set(k, v);
            return merged;
          });
        }
      } finally {
        if (generationRef.current === generation) {
          setIsResolving(false);
        }
      }
    })();
  }, [
    facilityId,
    supplierAccountId,
    currencyCode,
    lineItems,
    enabled,
    fetchPrice,
    resolveNonce,
  ]);

  return {
    resolvedPrices,
    isResolving,
    markUserOverride,
    pendingContextChange,
    confirmContextChange,
  };
}
